import { BaseRepository } from '@/Domain/Repository/BaseRepository'
import {
    AuditEvent,
    ProcessAttachmentsAggregate,
    ProductionLinksResult,
    Purchase,
    PurchasePayment,
    PurchaseStatus,
    PurchasingAttachment,
    PurchasePreparation,
    PurchaseProcess,
    PurchasingStage,
    PurchasingSupplier,
    SourceContext,
    SupplierQuote,
    SupplierQuoteStatus,
} from '@Purchasing/Domain/Model/PurchaseProcess'
import PurchasingDataSource from '../DataSource/PurchasingDataSource'
import {
    CreatePurchaseProcessPayload,
    CreatePurchasingSupplierPayload,
    CreateSupplierQuotePayload,
    FetchPurchaseProcessesParams,
    PaginatedPurchaseProcesses,
    UpdateProcessAssigneePayload,
    UpdateProcessStagePayload,
    UpdateProcessStatusPayload,
    UpdatePurchaseProcessPayload,
    UpdatePurchasingSupplierPayload,
    UpdateSupplierQuotePayload,
    BatchCreationResult,
    CreateProcessBatchPayload,
    CreatePurchasePayload,
    UpdatePurchasePayload,
    CreatePurchasePaymentPayload,
    MarkPaymentPaidPayload,
    UpdatePurchasePaymentPayload,
    InitiateAttachmentPayload,
    UpdateAttachmentPayload,
} from '../DataSource/API/Entity/PurchaseProcess'

export class PurchasingRepository
    implements BaseRepository<PurchaseProcess, CreatePurchaseProcessPayload>
{
    dataSource: PurchasingDataSource
    constructor(_dataSource: PurchasingDataSource) {
        this.dataSource = _dataSource
    }

    create(entity: CreatePurchaseProcessPayload): Promise<PurchaseProcess> {
        return this.dataSource.createProcess(entity)
    }

    delete(_id: string): Promise<void> {
        throw new Error('Method not supported.')
    }

    async findAll(): Promise<PurchaseProcess[]> {
        return this.dataSource.getProcesses({}).then((p) => p.items)
    }

    async findById(id: string): Promise<PurchaseProcess> {
        return this.dataSource.getProcessById(id)
    }

    async update(entity: PurchaseProcess): Promise<PurchaseProcess> {
        return this.dataSource.updateProcess(entity._id, {
            title: entity.title,
            need: entity.need,
            notes: entity.notes,
        })
    }

    getProcesses(
        params: FetchPurchaseProcessesParams
    ): Promise<PaginatedPurchaseProcesses> {
        return this.dataSource.getProcesses(params)
    }

    updateProcessStatus(
        id: string,
        payload: UpdateProcessStatusPayload
    ): Promise<PurchaseProcess> {
        return this.dataSource.updateProcessStatus(id, payload)
    }

    updateProcessStage(
        id: string,
        payload: UpdateProcessStagePayload
    ): Promise<PurchaseProcess> {
        return this.dataSource.updateProcessStage(id, payload)
    }

    updateProcessAssignee(
        id: string,
        payload: UpdateProcessAssigneePayload
    ): Promise<PurchaseProcess> {
        return this.dataSource.updateProcessAssignee(id, payload)
    }

    getProcessAudit(id: string): Promise<AuditEvent[]> {
        return this.dataSource.getProcessAudit(id)
    }

    getProduction(processId: string): Promise<ProductionLinksResult> {
        return this.dataSource.getProduction(processId)
    }

    refreshProduction(processId: string): Promise<void> {
        return this.dataSource.refreshProduction(processId)
    }

    confirmProduction(processId: string, productionId: string): Promise<void> {
        return this.dataSource.confirmProduction(processId, productionId)
    }

    unlinkProduction(processId: string, productionId: string): Promise<void> {
        return this.dataSource.unlinkProduction(processId, productionId)
    }

    getStages(): Promise<PurchasingStage[]> {
        return this.dataSource.getStages()
    }

    getSourceContext(
        code?: string,
        externalId?: string
    ): Promise<SourceContext> {
        return this.dataSource.getSourceContext(code, externalId)
    }

    getPreparation(params: {
        budgetCode?: number
        externalId?: string
    }): Promise<PurchasePreparation> {
        return this.dataSource.getPreparation(params)
    }

    createBatch(
        payload: CreateProcessBatchPayload
    ): Promise<BatchCreationResult> {
        return this.dataSource.createBatch(payload)
    }

    getPurchase(processId: string): Promise<Purchase | null> {
        return this.dataSource.getPurchase(processId)
    }

    createPurchase(
        processId: string,
        payload?: CreatePurchasePayload
    ): Promise<Purchase> {
        return this.dataSource.createPurchase(processId, payload)
    }

    updatePurchase(
        processId: string,
        payload: UpdatePurchasePayload
    ): Promise<Purchase> {
        return this.dataSource.updatePurchase(processId, payload)
    }

    updatePurchaseStatus(
        processId: string,
        status: PurchaseStatus
    ): Promise<Purchase> {
        return this.dataSource.updatePurchaseStatus(processId, status)
    }

    getPurchaseFinance(processId: string) {
        return this.dataSource.getPurchaseFinance(processId)
    }

    createPurchasePayment(
        processId: string,
        payload: CreatePurchasePaymentPayload
    ): Promise<PurchasePayment> {
        return this.dataSource.createPurchasePayment(processId, payload)
    }

    updatePurchasePayment(
        processId: string,
        paymentId: string,
        payload: UpdatePurchasePaymentPayload
    ): Promise<PurchasePayment> {
        return this.dataSource.updatePurchasePayment(
            processId,
            paymentId,
            payload
        )
    }

    markPurchasePaymentPaid(
        processId: string,
        paymentId: string,
        payload: MarkPaymentPaidPayload
    ): Promise<PurchasePayment> {
        return this.dataSource.markPurchasePaymentPaid(
            processId,
            paymentId,
            payload
        )
    }

    cancelPurchasePayment(
        processId: string,
        paymentId: string
    ): Promise<PurchasePayment> {
        return this.dataSource.cancelPurchasePayment(processId, paymentId)
    }

    getProcessAttachments(
        processId: string
    ): Promise<ProcessAttachmentsAggregate> {
        return this.dataSource.getProcessAttachments(processId)
    }

    getEntityAttachments(
        processId: string,
        entityType: string,
        entityId: string
    ): Promise<PurchasingAttachment[]> {
        return this.dataSource.getEntityAttachments(
            processId,
            entityType,
            entityId
        )
    }

    initiateAttachment(
        processId: string,
        payload: InitiateAttachmentPayload
    ): Promise<PurchasingAttachment> {
        return this.dataSource.initiateAttachment(processId, payload)
    }

    completeAttachment(
        processId: string,
        attachmentId: string
    ): Promise<PurchasingAttachment> {
        return this.dataSource.completeAttachment(processId, attachmentId)
    }

    updateAttachment(
        processId: string,
        attachmentId: string,
        payload: UpdateAttachmentPayload
    ): Promise<PurchasingAttachment> {
        return this.dataSource.updateAttachment(
            processId,
            attachmentId,
            payload
        )
    }

    deleteAttachment(
        processId: string,
        attachmentId: string
    ): Promise<{ removed: boolean; storageRemoved: boolean }> {
        return this.dataSource.deleteAttachment(processId, attachmentId)
    }

    getQuotes(processId: string): Promise<SupplierQuote[]> {
        return this.dataSource.getQuotes(processId)
    }

    createQuote(
        processId: string,
        payload: CreateSupplierQuotePayload
    ): Promise<SupplierQuote> {
        return this.dataSource.createQuote(processId, payload)
    }

    updateQuote(
        processId: string,
        quoteId: string,
        payload: UpdateSupplierQuotePayload
    ): Promise<SupplierQuote> {
        return this.dataSource.updateQuote(processId, quoteId, payload)
    }

    updateQuoteStatus(
        processId: string,
        quoteId: string,
        status: SupplierQuoteStatus
    ): Promise<SupplierQuote> {
        return this.dataSource.updateQuoteStatus(processId, quoteId, status)
    }

    selectQuote(processId: string, quoteId: string): Promise<PurchaseProcess> {
        return this.dataSource.selectQuote(processId, quoteId)
    }

    deleteQuote(processId: string, quoteId: string): Promise<void> {
        return this.dataSource.deleteQuote(processId, quoteId)
    }

    getSuppliers(params?: {
        active?: boolean
        search?: string
    }): Promise<PurchasingSupplier[]> {
        return this.dataSource.getSuppliers(params)
    }

    searchSuppliers(query: string): Promise<PurchasingSupplier[]> {
        return this.dataSource.searchSuppliers(query)
    }

    createSupplier(
        payload: CreatePurchasingSupplierPayload
    ): Promise<PurchasingSupplier> {
        return this.dataSource.createSupplier(payload)
    }

    updateSupplier(
        id: string,
        payload: UpdatePurchasingSupplierPayload
    ): Promise<PurchasingSupplier> {
        return this.dataSource.updateSupplier(id, payload)
    }

    getSource(): string {
        return this.dataSource.getSource()
    }
}
